// frontend/src/components/payments/PaymentHistory.js
import React, { useState } from 'react';
import { 
  Download, 
  Filter, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  Clock,
  RefreshCw,
  FileText,
  Receipt
} from 'lucide-react';

const PaymentHistory = ({ paymentHistory, error = false, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await onRefresh();
    } finally {
      setLoading(false);
    }
  };

  // Stripe hosts the PDF; the old /invoices/{id}/download route never existed.
  const handleDownloadInvoice = (payment) => {
    const url = payment.invoice_pdf || payment.hosted_invoice_url;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-danger" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-warning" />;
      default:
        return <Clock className="w-4 h-4 text-ink-muted" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'bg-success-tint text-success';
      case 'failed':
        return 'bg-danger-tint text-danger';
      case 'pending':
        return 'bg-warning-tint text-warning';
      default:
        return 'bg-surface-muted text-ink-soft';
    }
  };

  const formatAmount = (amount, currency = 'cad') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredHistory = paymentHistory.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'successful') return item.status === 'succeeded';
    if (filter === 'failed') return item.status === 'failed';
    if (filter === 'pending') return item.status === 'pending';
    return true;
  });

  if (error) {
    return (
      <div className="text-center py-12">
        <Receipt className="w-12 h-12 text-ink-muted mx-auto mb-4" strokeWidth={1.5} aria-hidden="true" />
        <h3 className="text-section-title font-display text-ink mb-2">Couldn't load your payment history</h3>
        <p className="text-body text-ink-muted mb-6">
          This is on our side, not your account. Try again in a moment.
        </p>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 min-h-touch px-4 rounded-control border border-border-strong bg-surface-card text-body font-semibold text-ink hover:bg-surface-sunken disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} aria-hidden="true" />
          Try again
        </button>
      </div>
    );
  }

  if (paymentHistory.length === 0) {
    return (
      <div className="text-center py-12">
        <Receipt className="w-12 h-12 text-ink-muted mx-auto mb-4" />
        <h3 className="text-lg font-medium text-ink-soft mb-2">No Payment History</h3>
        <p className="text-ink-muted mb-6">
          You haven't made any payments yet. Your payment history will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-ink-soft">Payment History</h2>
        <div className="flex items-center space-x-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-border-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
          >
            <option value="all">All Payments</option>
            <option value="successful">Successful</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 text-ink-muted hover:text-accent hover:bg-accent-tint rounded-lg transition-colors disabled:opacity-50"
            title="Refresh payment history"
            aria-label="Refresh payment history"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      <div className="bg-surface-card border border-border-default rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border-default">
            <thead className="bg-surface-page">
              <tr>
                <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-small font-medium text-ink-muted uppercase tracking-wider">
                  Invoice
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-card divide-y divide-border-default">
              {filteredHistory.map((payment) => (
                <tr key={payment.id} className="hover:bg-surface-page">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-ink-muted mr-2" />
                      <span className="font-mono tabular-nums">{formatDate(payment.created)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                    <div>
                      <div className="font-medium">{payment.description || 'Subscription Payment'}</div>
                      {payment.metadata?.plan && (
                        <div className="text-ink-muted text-small">
                          Plan: {payment.metadata.plan}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 text-ink-muted mr-1" />
                      <span className="font-mono tabular-nums">{formatAmount(payment.amount, payment.currency)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(payment.status)}
                      <span className={`ml-2 inline-flex px-2 py-1 text-small font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                    {(payment.invoice_pdf || payment.hosted_invoice_url) && (
                      <button
                        onClick={() => handleDownloadInvoice(payment)}
                        className="text-accent hover:text-accent-hover flex items-center"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-surface-page rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-ink-muted">Total Payments</div>
            <div className="font-medium text-ink-soft">{filteredHistory.length}</div>
          </div>
          <div>
            <div className="text-ink-muted">Total Amount</div>
            <div className="font-medium text-ink-soft">
              {formatAmount(
                filteredHistory.reduce((sum, payment) => sum + payment.amount, 0),
                filteredHistory[0]?.currency
              )}
            </div>
          </div>
          <div>
            <div className="text-ink-muted">Last Payment</div>
            <div className="font-medium text-ink-soft">
              {filteredHistory.length > 0 
                ? formatDate(filteredHistory[0].created) 
                : 'N/A'
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
